<template>
  <div class="chart-wrapper stacked-bar-chart">
    <h3 class="chart-title">{{ title }}</h3>
    <template v-if="loading">
      <div class="chart stacked-bar-chart__skeleton">
        <span
          v-for="n in 4"
          :key="n"
        />
      </div>
    </template>
    <BiChartEmpty v-else-if="!hasData" />
    <template v-else>
      <v-chart
        class="chart"
        :option="option"
        autoresize
      />
      <div class="stacked-bar-chart__legend">
        <span
          v-for="item in series"
          :key="item.name"
          class="stacked-bar-chart__legend-item"
        >
          <span
            class="stacked-bar-chart__legend-dot"
            :style="{ background: item.color }"
          />
          {{ item.name }}
        </span>
        <span
          v-if="referenceLineName"
          class="stacked-bar-chart__legend-item"
        >
          <span class="stacked-bar-chart__legend-line" />
          {{ referenceLineName }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import VChart from 'vue-echarts'
import BiChartEmpty from './BiChartEmpty.vue'
import { hasChartData } from './chartData'

export default {
  name: 'StackedBarChart',
  components: { VChart, BiChartEmpty },
  props: {
    title: { type: String, required: true },
    loading: { type: Boolean, default: false },
    categories: { type: Array, default: () => [] },
    series: { type: Array, default: () => [] },
    referenceLineName: { type: String, default: '' },
    referenceLineTooltipName: { type: String, default: '' },
    referenceLineValue: { type: Number, default: 0 },
  },
  computed: {
    hasData() {
      return this.series.some(item => hasChartData(item.data))
    },
    option() {
      const series = this.series.map(item => ({
        name: item.name,
        type: 'bar',
        stack: 'total',
        barMaxWidth: 25.5,
        data: item.data,
        itemStyle: { color: item.color },
      }))

      if (this.referenceLineName) {
        series.push({
          name: this.referenceLineName,
          type: 'line',
          data: Array(this.categories.length).fill(this.referenceLineValue),
          symbol: 'none',
          lineStyle: { color: '#333', width: 2 },
        })
      }

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: '#fff',
          borderColor: '#ddd',
          textStyle: { color: '#464646', fontSize: 14, fontFamily: 'Roboto' },
          formatter: params => {
            const title = `<div style="font-family:Roboto;font-weight:700;font-size:14px;color:#464646">${params[0].axisValueLabel}</div>`
            const lines = params
              .map(p => {
                const value =
                  p.value === null || p.value === undefined
                    ? '-'
                    : p.seriesType === 'line'
                      ? this.formatHours(
                          params
                            .filter(item => item.seriesType !== 'line')
                            .reduce((total, item) => total + (Number(item.value) || 0), 0)
                        )
                      : this.formatHours(p.value)
                const name =
                  p.seriesType === 'line' && this.referenceLineTooltipName
                    ? this.referenceLineTooltipName
                    : p.seriesName
                return `<div style="font-family:Roboto;font-size:14px;color:#464646">${name}: ${value}</div>`
              })
              .join('')
            return title + lines
          },
        },
        legend: { show: false },
        grid: { top: 12, left: 8, right: 10, bottom: 30, containLabel: true },
        xAxis: {
          type: 'category',
          data: this.categories,
          axisLabel: {
            fontSize: 12,
            fontWeight: 'normal',
            interval: 0,
            lineHeight: 14,
            color: '#5a5a5a',
          },
          axisLine: { lineStyle: { color: '#ddd' } },
          axisTick: { show: false },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            fontSize: 12,
            color: '#5a5a5a',
            formatter: value => this.formatHours(value),
          },
          splitLine: { lineStyle: { color: '#f0f0f0' } },
        },
        series,
      }
    },
  },
  methods: {
    formatHours(value) {
      const totalMinutes = Math.round(value * 60)
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      return minutes ? `${hours} h ${minutes} min` : `${hours} h`
    },
  },
}
</script>

<style scoped lang="scss">
.chart-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  box-sizing: border-box;
  padding: 16px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  font-family: 'Roboto', sans-serif;
}

.chart-title {
  margin: 0 0 12px;
  color: rgba(70, 70, 70, 1);
  font-size: 16px;
  font-weight: 700;
}

.chart {
  width: 100%;
  height: 220px;
}

.stacked-bar-chart__skeleton {
  display: flex;
  align-items: flex-end;
  gap: 16px;

  span {
    flex: 1;
    height: 70%;
    background: #e9e9e9;

    &:nth-child(even) {
      height: 90%;
    }
  }
}

.stacked-bar-chart__legend {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #444;
}

.stacked-bar-chart__legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stacked-bar-chart__legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.stacked-bar-chart__legend-line {
  width: 16px;
  height: 2px;
  background: #333;
  flex-shrink: 0;
}
</style>
